(ns app.domain.letter.model
  (:require [app.domain.inspection.templates-config :as ins-templates-config]
            [app.domain.letter.templates-config :as letter-templates-config]
            [app.domain.template.model :as template-model]
            [core.db.child-entity :as child]))

(def letter-fragment-types
  [:letter-fragment/html-template
   :letter-fragment/print-css

   :letter-fragment/property-owner-snippet
   :letter-fragment/applicant-snippet
   :letter-fragment/party-snippet

   :letter-fragment/violation-snippet
   :letter-fragment/compliant-violation-snippet
   :letter-fragment/non-compliant-violation-snippet

   :letter-fragment/paid-bill-snippet
   :letter-fragment/paid-bill-fee-group-snippet
   :letter-fragment/paid-bill-fee-item-snippet

   :letter-fragment/unpaid-bill-snippet
   :letter-fragment/unpaid-bill-fee-group-snippet
   :letter-fragment/unpaid-bill-fee-item-snippet

   :letter-fragment/no-access-inspection-snippet
   :letter-fragment/no-access-inspection-visit-snippet
   :letter-fragment/no-access-inspection-inspector-snippet
   :letter-fragment/no-access-inspection-checklist-table-snippet
   :letter-fragment/no-access-inspection-checklist-snippet
   :letter-fragment/no-access-inspection-checklist-group-snippet
   :letter-fragment/no-access-inspection-checklist-item-snippet
   :letter-fragment/no-access-inspection-deficiency-snippet
   :letter-fragment/no-access-inspection-photo-snippet

   :letter-fragment/not-complete-inspection-snippet
   :letter-fragment/not-complete-inspection-visit-snippet
   :letter-fragment/not-complete-inspection-inspector-snippet
   :letter-fragment/not-complete-inspection-checklist-table-snippet
   :letter-fragment/not-complete-inspection-checklist-snippet
   :letter-fragment/not-complete-inspection-checklist-group-snippet
   :letter-fragment/not-complete-inspection-checklist-item-snippet
   :letter-fragment/not-complete-inspection-deficiency-snippet
   :letter-fragment/not-complete-inspection-photo-snippet

   :letter-fragment/failed-inspection-snippet
   :letter-fragment/failed-inspection-visit-snippet
   :letter-fragment/failed-inspection-inspector-snippet
   :letter-fragment/failed-inspection-checklist-table-snippet
   :letter-fragment/failed-inspection-checklist-snippet
   :letter-fragment/failed-inspection-checklist-group-snippet
   :letter-fragment/failed-inspection-checklist-item-snippet
   :letter-fragment/failed-inspection-deficiency-snippet
   :letter-fragment/failed-inspection-photo-snippet

   :letter-fragment/passed-inspection-snippet
   :letter-fragment/passed-inspection-visit-snippet
   :letter-fragment/passed-inspection-inspector-snippet
   :letter-fragment/passed-inspection-checklist-table-snippet
   :letter-fragment/passed-inspection-checklist-snippet
   :letter-fragment/passed-inspection-checklist-group-snippet
   :letter-fragment/passed-inspection-checklist-item-snippet
   :letter-fragment/passed-inspection-deficiency-snippet
   :letter-fragment/passed-inspection-photo-snippet

   :letter-fragment/bond-snippet
   :letter-fragment/condition-snippet])

(def common-template-variables
  ["header"
   "letter-date"])

(def workspace-template-variables-data
  (-> [{:variable-name "form-data"}
       {:variable-name "workspace-created-date"}
       {:variable-name "application-number"}
       {:variable-name "case-number"}
       {:variable-name "licence-number"}
       {:variable-name "category"}
       {:variable-name "additional-categories"}
       {:variable-name "licence-type"}
       {:variable-name "registration-type"}
       {:variable-name "trades"}
       {:variable-name "assignee"}
       {:variable-name "address"}]
      (into (template-model/as-variable-name-maps template-model/address-placeholders))
      (into [{:variable-name "property-id"}
             {:variable-name "issued-to"}
             {:variable-name "holder-street-address"}
             {:variable-name "holder-postal-code"}
             {:variable-name "holder-municipality"}
             {:variable-name "holder-mobile-phone"}
             {:variable-name "holder-state-or-province"}
             {:variable-name "date-issued"}
             {:variable-name "valid-from"}
             {:variable-name "valid-to"}
             {:variable-name "case-description"}
             {:variable-name "legal-description"}
             {:variable-name "review-comments"}
             {:variable-name "latest-review-comments"}
             {:variable-name "applicants" :snippet? true}
             {:variable-name "property-owners" :snippet? true}
             {:variable-name "other-parties" :snippet? true}
             {:variable-name "unpaid-bills" :snippet? true}
             {:variable-name "paid-bills" :snippet? true}
             {:variable-name "no-access-inspections" :snippet? true}
             {:variable-name "not-complete-inspections" :snippet? true}
             {:variable-name "failed-inspections" :snippet? true}
             {:variable-name "passed-inspections" :snippet? true}
             {:variable-name "deficiencies" :snippet? true}
             {:variable-name "compliant-deficiencies" :snippet? true}
             {:variable-name "non-compliant-deficiencies" :snippet? true}
             {:variable-name "violations" :snippet? true}
             {:variable-name "compliant-violations" :snippet? true}
             {:variable-name "non-compliant-violations" :snippet? true}
             {:variable-name "bonds" :snippet? true}
             {:variable-name "conditions" :snippet? true}])))

(def workspace-template-variables
  (map :variable-name workspace-template-variables-data))

(def workspace-template-variables-wo-snippets
  (keep (fn [v]
          (when-not (:snippet? v)
            (:variable-name v)))
        workspace-template-variables-data))

(def licence-template-variables
  ["licence-status"
   "issued-to"
   "licence-type"
   "valid-from"
   "expiration-date"
   "issued-date"
   "licence-number"])

(def business-template-variables
  ["business-name"
   "trade-name"
   "business-type"
   "business-number"
   "mailing-address"
   "city"
   "state"
   "postal-code"
   "phone-number"])

(defn violation-snippet-variables [feature-flags]
  (into [] (concat
             ["inspection-name"]
             ["reference"]
             ["status"]
             ["description"]
             (when (:feature-flag.id/enhanced-template-configuration feature-flags)
               ["deficiency-number"
                "related-photos"])
             ["compliance-date"]
             ["comply-by-date"])))

(def bond-snippet-variables
  ["owner"
   "number"
   "issuer-name"
   "issuer-email"
   "expires-on"
   "amount"
   "type"
   "status"])

(def inspector-snippet-variables
  (ins-templates-config/variable-names-wo-snippet-variables
    ins-templates-config/inspector-snippet-variables))

(def visit-snippet-variables
  (ins-templates-config/variable-names-wo-snippet-variables
    ins-templates-config/visit-snippet-variables))

(def all-visit-variables
  (concat visit-snippet-variables
          ["inspectors"]))


(defn checklist-snippet-config
  "Creates checklist snippet config chain with specified entity/document storage references and titles."
  [ref-attr
   checklist-table-snippet-ref
   checklist-snippet-ref
   checklist-group-snippet-ref
   checklist-item-snippet-ref]
  (let [cfg-with-ref (fn [cfg-fn snippet-ref pdf-title & children]
                       (-> (apply cfg-fn children)
                           (dissoc :db-reference :fragment-reference)
                           (assoc ref-attr snippet-ref
                                  :pdf-title pdf-title)))]
    (cfg-with-ref ins-templates-config/table-for-checklists checklist-table-snippet-ref
                  :letter/inspection-checklist-table-snippet
                  (cfg-with-ref ins-templates-config/checklist-snippet checklist-snippet-ref
                                :letter/inspection-checklist-snippet
                                (cfg-with-ref ins-templates-config/checklist-group-snippet checklist-group-snippet-ref
                                              :letter/inspection-checklist-group-snippet
                                              (cfg-with-ref ins-templates-config/checklist-item-snippet
                                                            checklist-item-snippet-ref
                                                            :letter/inspection-checklist-item-snippet))))))

(defn deficiency-snippet-variables [group feature-flags]
  (ins-templates-config/variable-names-wo-snippet-variables
    (ins-templates-config/deficiency-snippet-variables group feature-flags)))

(defn photo-snippet-variables [feature-flags]
  (ins-templates-config/variable-names-wo-snippet-variables
    (ins-templates-config/photo-snippet-variables feature-flags)))

(def inspection-snippet-variables
  ["inspection-name"
   "inspection-status"])

(def all-inspection-variables
  (concat inspection-snippet-variables
          ["visits"
           "table-for-checklists"
           "deficiencies"
           "photos"]))

(def all-inspection-variables-ce
  (concat inspection-snippet-variables
          ["visits"
           "table-for-checklists"
           "violations"
           "photos"]))

(defn applicable-template-variables [{:keys [template-type] :as ctx}]
  (let [variables (case template-type
                    :letter-pdf-template.type/business
                    business-template-variables

                    :letter-pdf-template.type/licence
                    licence-template-variables

                    :letter-pdf-template.type/workspace
                    (filter #(letter-templates-config/workspace-letter-variable-applicable? % ctx)
                            workspace-template-variables)

                    [])]
    (concat common-template-variables
            variables)))

(def phase-letters ^{:arglists '([phase-entity])}
  (child/child-entities-of-type-fn :entity.type/letter))
